import React from 'react';
import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import reverse from 'lodash/reverse';
import ct from 'countries-and-timezones';
import {
  Drawer,
  Form,
  Input,
  Select,
  Card,
  Divider,
  Button,
  Popconfirm,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import moment from 'moment';

import useMedia from '../../hooks/useMedia';
import Description from './InfoBlocks/Description';
import Status from './InfoBlocks/Status';
import Sync from './InfoBlocks/Sync';
import Platform from './InfoBlocks/Platform';
import Orientation from './InfoBlocks/Orientation';
import Loader from '@components/snippets/Loader';
import { SWAGGER_URL } from '../../api';

const { Option } = Select;

const timezones = ct.getAllTimezones();

function DeviceEditForm({
  t,
  visible,
  logs,
  loading,
  device,
  calendars,
  screenshot,
  onClose,
  onEdit,
  onDelete,
  initialValues = {},
  onDeactivate,
  onGetScreenshot,
  onLogsRequest,
}) {
  const { isMobile } = useMedia();
  const {
    name,
    isActive,
    landscape,
    platform,
    version,
    lastOnline,
    problemSync,
    description,
    online,
    isActivate,
    lastSync,
    pin,
    percent,
  } = device;

  const sortedTimezones = reverse(sortBy(timezones, 'utcOffset'));

  return (
    <Drawer
      mask={false}
      title={<h2 className="device-edit__title">{t('edit-device')}</h2>}
      placement="right"
      width={!isMobile && visible ? 415 : 0}
      visible={visible}
      onClose={onClose}
      footer={
        <div className="device-edit__footer">
          {isActivate && (
            <Popconfirm
              placement="top"
              onConfirm={onDeactivate}
              title={t('deactivate-device-warning')}
              cancelText={t('cancel')}
              okText={t('deactivate')}
            >
              <Button type="danger" ghost>
                {t('deactivate-device')}
              </Button>
            </Popconfirm>
          )}
          <Popconfirm
            placement="top"
            onConfirm={onDelete}
            title={t('delete-device-warning')}
            cancelText={t('to-leave')}
            okText={t('delete')}
          >
            <Button type="danger">{t('delete-device')}</Button>
          </Popconfirm>
        </div>
      }
    >
      {loading ? (
        <div className="d-flex justify-center">
          <Loader loading={loading} />
        </div>
      ) : visible && !isEmpty(device) ? (
        <div className="device-edit">
          <Form
            onValuesChange={onEdit}
            layout="vertical"
            initialValues={initialValues}
          >
            <Form.Item name="name" label={t('device-name')}>
              <Input />
            </Form.Item>
            <Form.Item name="calendar" label={t('calendar-related')}>
              <Select placeholder={t('select')}>
                <Option
                  disabled={!initialValues.calendar}
                  key="null"
                  value={null}
                >
                  {t('not-chosen')}
                </Option>
                {map(calendars, ({ id, name, color }) => (
                  <Option key={id} value={id} label={name}>
                    {name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="timezone" label={t('timezone-label')}>
              <Select
                showSearch
                disabled={isActivate}
                placeholder={t('select')}
              >
                {map(sortedTimezones, ({ name, utcOffsetStr }) => (
                  <Option value={name}>{`${utcOffsetStr} ${name}`}</Option>
                ))}
              </Select>
            </Form.Item>
            <Description
              t={t}
              className="device-edit__alert"
              description={description}
              isActivate={isActivate}
              pin={pin}
            />
            <Card className="device-edit__info">
              <Status
                t={t}
                online={online}
                lastOnline={lastOnline}
                isActivate={isActivate}
              />
              <Sync
                t={t}
                lastSync={lastSync}
                problemSync={problemSync}
                percent={percent}
              />
              <Platform t={t} platform={platform} version={version} />
              <Orientation t={t} landscape={landscape} />
            </Card>
            <Divider className="device-edit__divider" />
            <div className="device-edit__screenshot">
              <h3 className="device-edit__screenshot-title">
                {t('screenshot')}
              </h3>
              <Button
                onClick={onGetScreenshot}
                className="device-edit__screenshot-btn"
              >
                {t('screenshot-request')}
              </Button>
              {!isEmpty(screenshot) && [
                <img
                  className="device-edit__screenshot-image"
                  src={
                    process.env.NODE_ENV === 'development'
                      ? SWAGGER_URL + screenshot.url
                      : screenshot.url
                  }
                  alt={name}
                />,
                <div className="device-edit__screenshot-date">
                  {moment(screenshot.createdAt).format('DD.MM.YYYY HH:mm')}
                </div>,
              ]}
              {isEmpty(screenshot) && (
                <div className="device-edit__no-screenshot">
                  {t('no-screenshot')} <br /> {t('request-screenshot')}
                </div>
              )}
            </div>
            <Divider className="device-edit__divider" />
            <div className="device-edit__logs">
              <h3 className="device-edit__logs-title">{t('logs')}</h3>
              <Button onClick={onLogsRequest} className="device-edit__logs-btn">
                {t('logs-request')}
              </Button>
              {!isEmpty(logs) ? (
                <ul className="device-edit__logs-list">
                  <li className="device-edit__logs-item">
                    <a
                      href={logs.url}
                      target="_blank"
                      className="device-edit__logs-link"
                    >
                      <DownloadOutlined className="device-edit__logs-icon" />
                      <span>
                        {t('log-from')}{' '}
                        {moment(logs.createdAt).format('DD.MM.YYYY HH:mm')}
                      </span>
                    </a>
                  </li>
                </ul>
              ) : (
                <div className="device-edit__no-screenshot">
                  {t('no-logs')} <br /> {t('request-logs')}
                </div>
              )}
            </div>
          </Form>
        </div>
      ) : null}
    </Drawer>
  );
}

export default DeviceEditForm;
